import { BufferedEventBus } from '../utils/BufferedEventBus';
import { CareMapState } from '../caremap/CareMapState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { Logger } from '../support/Logger';
import { APP_FOREGROUNDED } from '../AppContainer';
import { isExpectedFetchError } from '../support/isExpectedFetchError';
import { UserState } from '../user/UserState';

export class ForegroundReconciler {
    static inject = () => [BufferedEventBus, CareMapState, CaremapNotesModelFactory, Logger, UserState];
    constructor(
        private events: BufferedEventBus,
        private careMapState: CareMapState,
        private factory: CaremapNotesModelFactory,
        private logger: Logger,
        private userState: UserState
    ) {}

    private inFlight = false;
    private started = false;

    start = () => {
        // guard against double-mount (e.g. React remount) re-subscribing
        if (this.started) {
            return;
        }
        this.started = true;

        // Two independent convergence triggers (spec §4): app return to foreground, and a fresh
        // socket connection (also fires on reconnect after a drop). Either means we may have missed
        // changes, so we run a full catch-up of the active caremap + its notes. Handlers are
        // idempotent (re-fetch from source), satisfying the BufferedEventBus replay contract.
        this.events.on(APP_FOREGROUNDED, this.reconcile);
        this.events.on('web-sockets.connected', this.reconcile);
    };

    private reconcile = async () => {
        // coalesce overlapping triggers (foreground + connected often fire together)
        if (this.inFlight) {
            return;
        }

        const activeId = this.careMapState.getActiveCaremap()?.careMapId;
        if (!activeId) {
            // nothing active to reconcile (anonymous bootstrap, or no caremap selected yet)
            return;
        }

        this.inFlight = true;
        try {
            // best-effort — a failed account reload must not block the caremap reconcile.
            // userState.reload() catches expected (transient) errors itself; wrap here so an
            // unexpected throw also doesn't abort the caremap reconcile that follows.
            try {
                await this.userState.reload();
            } catch (err: any) {
                this.logger.error(`ForegroundReconciler: userState.reload failed: ${err.toString()}`);
            }

            await this.careMapState.reload(activeId);
            // the active caremap can be cleared (logout / navigation) during the awaited reload; if so
            // forActiveCaremap() would throw "there is no active caremap" — nothing left to reconcile.
            if (!this.careMapState.getActiveCaremap()) {
                return;
            }
            await this.factory.forActiveCaremap().reconcile();
            // tell open editors (Slice E) the active note may have a newer server version
            this.events.emit('ForegroundReconciler.reconciled');
        } catch (err: any) {
            // a transient/aborted reconcile fetch is expected — it retries on the next trigger.
            // Alert only on genuine failures.
            if (!isExpectedFetchError(err)) {
                this.logger.error(`ForegroundReconciler.reconcile failed: ${err.toString()}`);
            }
        } finally {
            this.inFlight = false;
        }
    };
};
